import { type ReactNode, type ReactElement } from 'react'
import * as styles from './Sidebar.css'
import { Row, Stack } from '@christiankaindl/lyts'
import {
  ChevronDown,
  ExternalLink,
  Info,
  MoreHorizontalIcon,
} from 'lucide-react'
import Button from '@shared/components/Button/Button'
import SearchButton from './SearchButton'
import { infoOverlayStore } from '@/components/Norm/Utils'
import { Menu, MenuButton, MenuProvider } from '@shared/components/Menu'
import { MenuItem } from '@shared/components/Menu/MenuItem'
import { ActionRow, Header } from './Sidebar.client'
import { useNormContext } from '@/components/Norm/NormContext'
import deGesetzeImInternetLogo from '@shared/assets/gesetze-im-internet-de-logo.png'
import rechtBundDeLogo from '@shared/assets/recht-bund-de-logo.png'
import risLogo from '@shared/assets/ris-logo.png'
import findokLogo from '@shared/assets/findok-logo.png'
import eurlexLogo from '@shared/assets/eurlex-logo.svg'
import parlamentGvLogo from '@shared/assets/parlament-gv-logo.svg'
import Text from '@shared/components/Text/Text'

interface SidebarContentProps {
  title: string
  menu?: ReactNode
  children: ReactElement<any>
  forceShowTitle?: boolean
  onSelect?: () => void
  showSearch?: boolean
  normId: string // TODO: Replace with norm context provider
  abbrNr?: number | null // TODO: Replace with norm context provider
  headerClassName?: string
  headerButtons?: ReactNode
  noToc?: boolean
  abbreviation?: string
  content?: ReactElement<any>
}
export default function SidebarContent({
  title,
  menu,
  children,
  forceShowTitle = false,
  showSearch = false,
  headerClassName,
  headerButtons,
  noToc = false,
  abbreviation,
  content,
}: SidebarContentProps) {
  return (
    <Stack gap={1.5} className={styles.content}>
      <Header
        forceShowTitle={forceShowTitle}
        title={title}
        abbreviation={abbreviation}
        className={headerClassName}
      >
        <ActionRow
          menu={menu}
          showSearch={showSearch}
          headerButtons={headerButtons}
          noToc={noToc}
        />
      </Header>
      <Stack gap='6px' style={{ paddingLeft: 9, marginTop: -18 }}>
        {headerButtons}
        {showSearch && <SearchButton size='medium' />}
        {!noToc && (
          <Button
            variant='flat'
            icon={Info}
            onClick={() => {
              infoOverlayStore.set({ isOpen: true })
            }}
            style={{ gap: '0.8rem' }}
          >
            Info
          </Button>
        )}
        <Menu>
          <MenuButton variant='flat' title='Optionen' style={{ gap: '0.8rem' }}>
            <MoreHorizontalIcon size={20} />
            Mehr
          </MenuButton>
          {menu}
        </Menu>

        <SourceProviderButton />
      </Stack>
      {content}
      {children}
    </Stack>
  )
}

function SourceProviderButton() {
  const { source, sourceUrl, risUrl, formats, documentType } = useNormContext()

  const formatEntries = Object.entries(formats ?? {}).filter(
    ([, url]) => typeof url === 'string' && url.length > 0,
  )

  const formatLabel: Record<string, string> = {
    html: 'HTML',
    pdf: 'PDF',
    xml: 'XML',
    www: 'WWW',
    epub: 'EPUB',
  }

  const sourceButton = (() => {
    const externalLinkIcon = (
      <ExternalLink size={14} style={{ opacity: 0.4, marginLeft: -3 }} />
    )
    switch (source) {
      case 'findok':
      case 'at.findok':
        return (
          <Button
            variant='flat'
            href={sourceUrl}
            style={{ gap: '0.8rem' }}
            title='Quelle'
          >
            <img src={findokLogo.src} height={20} width={20} alt='' />
            Findok
            {externalLinkIcon}
          </Button>
        )
      case 'eurlex':
      case 'eu.eurlex':
        return (
          <Button
            variant='flat'
            href={sourceUrl}
            style={{ gap: '0.8rem' }}
            title='Quelle'
          >
            <img src={eurlexLogo.src} height={20} width={20} alt='' />
            EUR-Lex
            {externalLinkIcon}
          </Button>
        )
      case 'at.parlament':
        return (
          <Button
            variant='flat'
            href={sourceUrl}
            style={{ gap: '0.8rem' }}
            title='Quelle'
          >
            <img src={parlamentGvLogo.src} height={20} width={20} alt='' />
            Parlament.gv
            {externalLinkIcon}
          </Button>
        )
      case 'ris':
      case 'at.ris':
        return (
          <Button
            variant='flat'
            href={sourceUrl ?? risUrl}
            style={{ gap: '0.8rem' }}
            title='Quelle'
          >
            <img src={risLogo.src} height={20} width={20} alt='' />
            RIS
            {externalLinkIcon}
          </Button>
        )
      case 'de.ris': {
        const isGazette = documentType === 'gazette'
        return (
          <Button
            variant='flat'
            href={sourceUrl}
            style={{ gap: '0.8rem' }}
            title='Quelle'
          >
            <img
              src={
                isGazette ? rechtBundDeLogo.src : deGesetzeImInternetLogo.src
              }
              height={20}
              width={20}
              alt=''
            />
            {isGazette ? 'Bundesgesetzblatt' : 'Gesetze im Internet'}
            {externalLinkIcon}
          </Button>
        )
      }
      default:
        return null
    }
  })()

  if (!sourceButton || formatEntries.length === 0) return sourceButton

  return (
    <Row gap={0}>
      {sourceButton}
      <MenuProvider>
        <MenuButton
          variant='flat'
          style={{ minWidth: 24 }}
          square
          title='Downloads'
        >
          <ChevronDown size={14} />
        </MenuButton>
        <Menu>
          <div style={{ padding: '3px 12px 0px' }}>
            <Text size='small' color='tertiary'>
              Downloads
            </Text>
          </div>
          {formatEntries.map(([key, url]) => (
            <MenuItem key={key} href={url} target='_blank' rel='noreferrer'>
              {formatLabel[key] ?? key.toUpperCase()}
            </MenuItem>
          ))}
        </Menu>
      </MenuProvider>
    </Row>
  )
}
